import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@elkaso-app/web-design';
import React, { useCallback } from 'react';

import { ICrmBrandContactDetailsData, useDeleteCrmBrandContactDetailsApi } from 'apis/crm-brand-contact-details';

interface DeleteContactDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactDetails: ICrmBrandContactDetailsData;
}

export const DeleteContactDetailsModal: React.FC<DeleteContactDetailsModalProps> = ({
  isOpen,
  onClose,
  contactDetails,
}) => {
  // Self-contained mutation with success handling
  const { mutate: deleteContactDetails, isLoading: isDeleting } = useDeleteCrmBrandContactDetailsApi({
    onSuccess: () => {
      onClose();
    },
  });

  // Memoized confirm handler
  const handleConfirm = useCallback(() => {
    deleteContactDetails({ id: contactDetails.id });
  }, [deleteContactDetails, contactDetails.id]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='md' closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Delete Contact Details</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align='start'>
            <Text>
              Are you sure you want to delete the contact details for{' '}
              <Text as='span' fontWeight='bold' color='red.600'>
                {contactDetails.contactName}
              </Text>
              ?
            </Text>

            <VStack spacing={2} align='start' bg='gray.50' p={4} borderRadius='md' w='full'>
              <Text fontSize='sm' color='gray.600'>
                <Text as='span' fontWeight='semibold'>
                  Brand:
                </Text>{' '}
                {contactDetails.brand?.name}
              </Text>
              <Text fontSize='sm' color='gray.600'>
                <Text as='span' fontWeight='semibold'>
                  Contact:
                </Text>{' '}
                {contactDetails.contactName}
              </Text>
              {contactDetails.email && (
                <Text fontSize='sm' color='gray.600'>
                  <Text as='span' fontWeight='semibold'>
                    Email:
                  </Text>{' '}
                  {contactDetails.email}
                </Text>
              )}
              {contactDetails.phoneNumber && (
                <Text fontSize='sm' color='gray.600'>
                  <Text as='span' fontWeight='semibold'>
                    Phone:
                  </Text>{' '}
                  {contactDetails.phoneNumber}
                </Text>
              )}
            </VStack>

            <Text fontSize='sm' color='red.600' fontWeight='medium'>
              This action cannot be undone.
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant='outline' onClick={onClose} isDisabled={isDeleting}>
              Cancel
            </Button>
            <Button colorScheme='red' onClick={handleConfirm} isLoading={isDeleting} loadingText='Deleting...'>
              Delete Contact Details
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
